import { ConfirmModal, TablePaginationContext, UnLink } from '@/components';
import { RoleColumn } from '@/components/Columns/RoleColumn';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { RoutePath } from '@/core/router';
import { UserBusinessArea } from '@/modules/BusinessAreas/domain';
import { AttachUserToBusinessModal } from '@/modules/BusinessAreas/pages/BusinessAreasPage/components/AttachUserToBusinessModal';
import { useGetBusinessAreasByUser } from '@/modules/BusinessAreas/queries';
import { useDisassociateBusinessAreaUser } from '@/modules/BusinessAreas/queries/disassociate-user';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export type UserEmployeeTabProps = {
  userId: number;
};

export const UserEmployeeTab = ({ userId }: UserEmployeeTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedBusinessAreaId, setSelectedBusinessAreaId] =
    useState<number>();
  const {
    page: pageNumber,
    searchTerm,
    tableSort,
  } = useContext(TablePaginationContext);
  const sort = tableSort[0];

  const { data: businessAreas, isLoading } = useGetBusinessAreasByUser({
    userId,
    filter: searchTerm,
    pageNumber: pageNumber,
    sortBy: sort?.columnId,
    sortOrder: sort?.direction,
  });

  const { mutate: disassociateUserBusinessArea } =
    useDisassociateBusinessAreaUser();

  const columns = useMemo<SortableColumnDef<UserBusinessArea>[]>(
    () => [
      {
        id: 'organization',
        name: t('organization'),
        accessor: 'organization',
      },
      {
        id: 'company',
        name: t('company'),
        accessor: 'company',
      },
      {
        id: 'business_area',
        name: t('business_area'),
        accessor: 'name',
      },
      {
        id: 'role',
        name: t('role'),
        accessor: 'roleName',
        renderRow: (row) => (
          <RoleColumn roleName={row.roleName} roleEntity="business-area" />
        ),
      },
      {
        id: 'actions',
        name: '',
        renderRow: (row) => (
          <UnLink
            className="cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedBusinessAreaId(row.id);
            }}
          />
        ),
      },
    ],
    [t],
  );

  const page = businessAreas && {
    ...businessAreas,
    items: businessAreas.items.map((item) => ({
      ...item,
      onClick: () => navigate(RoutePath.businessAreaDetail(item.id.toString())),
    })),
  };

  return (
    <>
      <PagedTable
        title={t('employee')}
        emptyStateMessage={t('no_business_areas_available')}
        columns={columns}
        page={page}
        isLoading={isLoading}
        actions={<AttachUserToBusinessModal userId={userId} />}
      />
      <ConfirmModal
        isOpen={!!selectedBusinessAreaId}
        title={t('please_confirm_action')}
        message={t('confirm_user_business_area_disassociate')}
        handleClose={() => setSelectedBusinessAreaId(undefined)}
        onConfirm={() => {
          if (selectedBusinessAreaId) {
            disassociateUserBusinessArea({
              businessAreaId: selectedBusinessAreaId,
              userId,
            });
          }
          setSelectedBusinessAreaId(undefined);
        }}
      />
    </>
  );
};
